import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n/index.js';
import { revisarVersion } from '../lib/actualizacion.js';
import { useSesion } from '../lib/sesion.js';
import { Entrar } from '../routes/Entrar.js';
import { useRevision, VersionNueva } from './VersionNueva.js';

/** Backstop for a tab that runs no animations: the door is out of the way by then regardless. */
const ESPERA_MAXIMA_MS = 1000;

/**
 * La puerta: the PIN pad over the app, for signing in and for unlocking.
 *
 * Over the app rather than instead of it, for two reasons. A sale half rung up, a purchase half
 * typed in, are still there when the same person unlocks. And once the PIN is right the pad can
 * dissolve into the app behind it (`saliendo`) instead of cutting to it — which is also why
 * signing in from the list uses this, and not a page of its own: with nothing behind it there
 * would be nothing to dissolve into.
 *
 * Opaque, because the screen underneath may be the owner's reports, and it appears at once,
 * without a fade, for the same reason. While it is shut everything else on the page is inert —
 * the screen and any sheet open on it — so the pad is the only thing a tap, a keyboard or a
 * screen reader can reach. The moment it starts to open, the app answers again.
 *
 * Somebody else unlocking starts at the home screen, not in the middle of another person's work.
 *
 * And once the PIN is right, or when the app opens with no PIN to ask for, the door stays shut
 * while the app checks for a new version (`lib/actualizacion.ts`): nothing of the app shows
 * before it has. Usually the answer is in before the greeting has finished, which is why the
 * check starts the moment the PIN is right; one that takes longer says what it is waiting for.
 * A new version takes the door over (`VersionNueva.tsx`), and «Actualizar» is the way through.
 */
export function Puerta({
  saliendo,
  pidePin,
  onAlDia,
  onFuera,
  onOtraPersona,
}: {
  /** In and up to date: dissolve, then call `onFuera`. */
  saliendo: boolean;
  /** Somebody has to type a PIN. Otherwise the door is checking for a new version. */
  pidePin: boolean;
  /** Checked, and nothing stands in the way: the door may open. */
  onAlDia: () => void;
  onFuera: () => void;
  onOtraPersona: () => void;
}) {
  const t = useT();
  const sesion = useSesion();
  // Somebody signed in behind a shut door means a lock, and theirs is the pad it opens on.
  const [quien] = useState(() => sesion?.usuarioId);
  // Once a PIN pad, its greeting stays on screen while the check runs.
  const [conPin, setConPin] = useState(pidePin);
  if (pidePin && !conPin) setConPin(true);
  const revision = useRevision(!pidePin, onAlDia);
  const nueva = !pidePin && revision.estado !== 'revisando' ? revision.estado : null;
  const capa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (saliendo) return;
    const otros = [...document.body.children].filter(
      (el) => el !== capa.current && !el.hasAttribute('inert'),
    );
    for (const el of otros) el.setAttribute('inert', '');
    return () => {
      for (const el of otros) el.removeAttribute('inert');
    };
  }, [saliendo]);

  useEffect(() => {
    if (!saliendo) return;
    const reloj = window.setTimeout(onFuera, ESPERA_MAXIMA_MS);
    return () => window.clearTimeout(reloj);
  }, [saliendo]);

  return createPortal(
    <div
      ref={capa}
      className={`fondo fixed inset-0 z-[70] overflow-y-auto overscroll-contain ${
        saliendo ? 'se-abre pointer-events-none' : ''
      }`}
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget && e.animationName === 'se-abre') onFuera();
      }}
    >
      {nueva ? (
        <VersionNueva estado={nueva} onActualizar={revision.actualizar} onSeguir={onAlDia} />
      ) : conPin ? (
        <Entrar
          usuarioInicial={quien}
          onPinCorrecto={() => void revisarVersion()}
          onEntro={(s) => {
            if (quien && s.usuarioId !== quien) onOtraPersona();
          }}
        />
      ) : (
        // Opening with nobody to ask for a PIN: the loading screen, as it was a moment ago.
        <div className="flex min-h-dvh items-center justify-center">
          <p role="status" className="text-sm text-slate-500">
            {t(revision.tarda ? 'actualizar.revisando' : 'comun.abriendo')}
          </p>
        </div>
      )}
      {conPin && !pidePin && !nueva && revision.tarda && (
        <p
          role="status"
          className="fixed inset-x-0 bottom-[max(env(safe-area-inset-bottom),1.5rem)] px-6 text-center text-sm text-slate-400"
        >
          {t('actualizar.revisando')}
        </p>
      )}
    </div>,
    document.body,
  );
}
